import { ref } from 'vue';
import DealerRepository from '@/repositories/dealer/DealerRepository.ts';
import {
    Dealer, DealerBrand, DealerCreateForm, DealerListItem,
    DealerPaginatorItem,
    DealersPaginator,
    DealersRequestFilter, DealerUpdateForm,
} from '@/types/dealer.type.ts';
import { LegalEntityUpdateForm } from '@/types/legal-entity.type.ts';
import { PaginationMeta } from '@/types/pagination-meta.ts';
import { User } from '@/types/user.type.ts';
import useLoading from '@/utils/useLoading.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useDealerRepository() {
    const arDealers = ref<DealerPaginatorItem[]>([]);
    const dealersPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isDealersLoading, setLoading: setDealersLoading } = useLoading();
    const { isLoading: isDealerLoading, setLoading: setDealerLoading } = useLoading();
    const dealerRepository = new DealerRepository();

    const getDealers = (requestFilter?: DealersRequestFilter): void => {
        setDealersLoading(true);

        dealerRepository.getDealers(requestFilter)
            .then((dealersPaginator: DealersPaginator) => {
                arDealers.value = dealersPaginator.data;
                dealersPaginationMeta.value = dealersPaginator.meta;
                setDealersLoading(false);
            });
    };

    const getDealer = (id: number): Promise<Dealer> => {
        setDealerLoading(true);

        return dealerRepository.getDealer(id)
            .then((dealer) => {
                setDealerLoading(false);

                return dealer;
            });
    };

    const createDealer = (dealerCreateForm: DealerCreateForm): Promise<void> => {
        return dealerRepository.createDealer(dealerCreateForm);
    };

    const updateDealer = (id: number, dealerUpdateForm: DealerUpdateForm): Promise<void> => {
        return dealerRepository.updateDealer(id, dealerUpdateForm);
    };

    const getDealerBrands = (): Promise<DealerBrand[]> => {
        return dealerRepository.getDealerBrands();
    };

    const activateDealer = (id: number): Promise<void> => {
        return dealerRepository.activateDealer(id);
    };

    const deactivateDealer = (id: number): Promise<void> => {
        return dealerRepository.deactivateDealer(id);
    };

    const getDealerUsers = (id: number): Promise<User[]> => {
        return dealerRepository.getDealerUsers(id);
    };

    const getAllActiveDealers = (): Promise<DealerListItem[]> => {
        return dealerRepository.getAllActiveDealers();
    };

    const updateDealerLegalEntity = (id: number, form: LegalEntityUpdateForm): Promise<void> => {
        return dealerRepository.updateDealerLegalEntity(id, form);
    };

    return {
        arDealers,
        dealersPaginationMeta,
        isDealersLoading,
        isDealerLoading,
        getDealers: getDealers,
        getDealer: getDealer,
        createDealer: createDealer,
        updateDealer: updateDealer,
        getDealerBrands: getDealerBrands,
        activateDealer: activateDealer,
        deactivateDealer: deactivateDealer,
        getDealerUsers: getDealerUsers,
        getAllActiveDealers: getAllActiveDealers,
        updateDealerLegalEntity: updateDealerLegalEntity,
    };
}
